import dayjs from 'dayjs';
import type { OsceStationPhase } from '@/graphql/gql/graphql';

const END_OF_TURN_MS = 400;

const USER_ROLES = new Set(['user', 'human']);
const AGENT_ROLES = new Set(['agent', 'assistant', 'ai']);

export interface OsceChatMessageMetrics {
  e2e_latency?: number;
  llm_node_ttft?: number;
  tts_node_ttfb?: number;
  end_of_turn_delay?: number;
}

export interface OsceChatMessage {
  role?: string;
  content?: string[];
  created_at?: number;
  transcript_confidence?: number;
  metrics?: OsceChatMessageMetrics;
}

export interface TranscriptMessagePayload {
  id?: string | null;
  type?: string | null;
  content?: string | null;
  createdAt?: unknown;
  chat_message?: unknown;
  phase?: OsceStationPhase | null;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const toUnixSeconds = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value > 1e12 ? value / 1000 : value;
  }
  if (typeof value === 'string') {
    const parsed = Date.parse(value);
    if (!Number.isNaN(parsed)) return parsed / 1000;
  }
  return null;
};

export const parseOsceChatMessage = (raw: unknown): OsceChatMessage | null => {
  if (!isRecord(raw)) return null;

  const content = Array.isArray(raw.content)
    ? raw.content.filter((part): part is string => typeof part === 'string')
    : undefined;

  const metrics = isRecord(raw.metrics)
    ? {
        e2e_latency: typeof raw.metrics.e2e_latency === 'number' ? raw.metrics.e2e_latency : undefined,
        llm_node_ttft: typeof raw.metrics.llm_node_ttft === 'number' ? raw.metrics.llm_node_ttft : undefined,
        tts_node_ttfb: typeof raw.metrics.tts_node_ttfb === 'number' ? raw.metrics.tts_node_ttfb : undefined,
        end_of_turn_delay:
          typeof raw.metrics.end_of_turn_delay === 'number' ? raw.metrics.end_of_turn_delay : undefined,
      }
    : undefined;

  return {
    role: typeof raw.role === 'string' ? raw.role : undefined,
    content,
    created_at: typeof raw.created_at === 'number' ? raw.created_at : undefined,
    transcript_confidence:
      typeof raw.transcript_confidence === 'number' ? raw.transcript_confidence : undefined,
    metrics,
  };
};

export const getMessageRole = (
  chat: OsceChatMessage | null,
  fallbackType?: string | null,
): 'user' | 'agent' => {
  const role = chat?.role ?? fallbackType ?? '';
  if (USER_ROLES.has(role)) return 'user';
  if (AGENT_ROLES.has(role)) return 'agent';
  return fallbackType === 'user' || fallbackType === 'human' ? 'user' : 'agent';
};

export const getMessageContent = (chat: OsceChatMessage | null, fallbackContent?: string | null): string => {
  if (chat?.content?.length) return chat.content.join('');
  return fallbackContent ?? '';
};

export const getMessageElapsedSec = (
  chat: OsceChatMessage | null,
  fallbackCreatedAt?: unknown,
  sessionStartSec?: number | null,
): number | null => {
  const messageSec = chat?.created_at ?? toUnixSeconds(fallbackCreatedAt);
  if (messageSec == null) return null;
  if (sessionStartSec == null) return messageSec;
  return Math.max(0, messageSec - sessionStartSec);
};

export const getAgentLatencyMs = (metrics?: OsceChatMessageMetrics): number | null => {
  if (!metrics) return null;

  if (typeof metrics.e2e_latency === 'number' && Number.isFinite(metrics.e2e_latency)) {
    return Math.round(metrics.e2e_latency * 1000);
  }

  const llm = metrics.llm_node_ttft ?? 0;
  const tts = metrics.tts_node_ttfb ?? 0;
  if (llm === 0 && tts === 0) return null;

  return Math.round((llm + tts) * 1000 + END_OF_TURN_MS);
};

export type AgentLatencyTier = 'green' | 'yellow' | 'orange' | 'red';

export const AGENT_LATENCY_TIERS: AgentLatencyTier[] = ['green', 'yellow', 'orange', 'red'];

export const getAgentLatencyTier = (latencyMs: number): AgentLatencyTier => {
  if (latencyMs < 1800) return 'green';
  if (latencyMs < 2200) return 'yellow';
  if (latencyMs < 2700) return 'orange';
  return 'red';
};

export const getUserEndOfTurnDelayMs = (metrics?: OsceChatMessageMetrics): number | null => {
  if (!metrics) return null;
  if (typeof metrics.end_of_turn_delay === 'number' && Number.isFinite(metrics.end_of_turn_delay)) {
    return Math.round(metrics.end_of_turn_delay * 1000);
  }
  return null;
};

export const getUserDelayTier = (delayMs: number): AgentLatencyTier => {
  if (delayMs < 500) return 'green';
  if (delayMs < 600) return 'yellow';
  if (delayMs < 700) return 'orange';
  return 'red';
};

export const formatSessionElapsed = (elapsedSec: number): string => {
  const totalCs = Math.max(0, Math.round(elapsedSec * 100));
  const minutes = Math.floor(totalCs / 6000);
  const seconds = Math.floor((totalCs % 6000) / 100);
  const centiseconds = totalCs % 100;

  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}.${String(centiseconds).padStart(2, '0')}`;
};

export const formatTranscriptConfidence = (value: number | null | undefined): string => {
  if (value == null) return '—';
  const normalized = value <= 1 ? value * 100 : value;
  return `${normalized.toFixed(1)}%`;
};

export const getEarliestMessageSec = (
  messages: Array<{ chat_message?: unknown; createdAt?: unknown } | null | undefined>,
): number | null => {
  let earliest: number | null = null;

  for (const msg of messages) {
    if (!msg) continue;
    const chat = parseOsceChatMessage(msg.chat_message);
    const sec = chat?.created_at ?? toUnixSeconds(msg.createdAt);
    if (sec != null && (earliest == null || sec < earliest)) {
      earliest = sec;
    }
  }

  return earliest;
};

export const formatChatMessageTimestamp = (unixSec: number): string => {
  const ms = Math.round((unixSec % 1) * 1000);
  return `${dayjs.unix(Math.floor(unixSec)).format('DD MMM YYYY, HH:mm:ss')}.${String(ms).padStart(3, '0')}`;
};

export const getChatMessageModalPayload = (message: TranscriptMessagePayload) => {
  const chat = parseOsceChatMessage(message.chat_message);
  const timestampSec = chat?.created_at ?? toUnixSeconds(message.createdAt);
  const data =
    message.chat_message ??
    ({
      id: message.id,
      type: message.type,
      content: message.content,
      createdAt: message.createdAt,
    } as const);

  return { data, timestampSec };
};
